import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface Props {
  label: string;
  active?: boolean;
  align?: "left" | "right";
  children?: ReactNode;
  className?: string;
}

type PopupPosition = {
  top: number;
  left: number;
  width: number;
};

export default function HeaderFilterCell({
  label,
  active = false,
  align = "left",
  children,
  className = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PopupPosition>({ top: 0, left: 0, width: 288 });
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const updatePosition = () => {
    const button = buttonRef.current;
    if (!button || typeof window === "undefined") return;

    const rect = button.getBoundingClientRect();
    const popupWidth = 288;
    const pagePadding = 12;
    const preferredLeft = align === "right" ? rect.right - popupWidth : rect.left;
    const maxLeft = window.innerWidth - popupWidth - pagePadding;
    const nextLeft = Math.max(pagePadding, Math.min(preferredLeft, maxLeft));

    setPosition({
      top: rect.bottom + 8,
      left: nextLeft,
      width: popupWidth,
    });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, align]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || popupRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    function handleViewportChange() {
      updatePosition();
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open, align]);

  return (
    <div ref={rootRef} className={`relative px-2 py-2 ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex w-full items-center gap-1 text-left transition-colors ${
          active || open ? "text-farm-brown" : "text-farm-muted hover:text-farm-brown"
        }`}
      >
        <span className="truncate">{label}</span>
        <i
          className={`text-xs ${
            active ? "ri-filter-3-fill text-farm-earth" : "ri-filter-3-line"
          }`}
        />
      </button>

      {open && children && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={popupRef}
              className="fixed z-[9999] rounded-2xl border border-farm-border bg-white p-3 shadow-xl"
              style={{ top: position.top, left: position.left, width: position.width }}
              onClick={(event) => event.stopPropagation()}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export function FilterSearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
    />
  );
}

export function FilterRangeInputs({
  min,
  max,
  onMinChange,
  onMaxChange,
  minPlaceholder = "Min",
  maxPlaceholder = "Max",
}: {
  min: string;
  max: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  minPlaceholder?: string;
  maxPlaceholder?: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <input
        value={min}
        onChange={(event) => onMinChange(event.target.value)}
        placeholder={minPlaceholder}
        inputMode="decimal"
        className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
      />
      <input
        value={max}
        onChange={(event) => onMaxChange(event.target.value)}
        placeholder={maxPlaceholder}
        inputMode="decimal"
        className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
      />
    </div>
  );
}

export function FilterOptionList({
  options,
  selected,
  onToggle,
}: {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="max-h-56 space-y-1 overflow-y-auto pr-1">
      {options.length === 0 ? (
        <div className="rounded-xl border border-dashed border-farm-border px-3 py-4 text-center text-xs text-farm-muted">
          Seçenek yok
        </div>
      ) : (
        options.map((option) => {
          const checked = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => onToggle(option)}
              className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-sm transition-colors ${
                checked
                  ? "border-farm-earth bg-farm-earth-pale text-farm-brown"
                  : "border-farm-border bg-farm-cream text-farm-muted hover:text-farm-brown"
              }`}
            >
              <span className="truncate">{option}</span>
              <i
                className={checked ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"}
              />
            </button>
          );
        })
      )}
    </div>
  );
}

export function FilterFooter({
  onClear,
  hint,
}: {
  onClear: () => void;
  hint?: string;
}) {
  return (
    <div className="mt-3 flex items-center justify-between gap-2 border-t border-farm-border pt-3">
      <span className="text-[11px] text-farm-muted">{hint ?? ""}</span>
      <button
        type="button"
        onClick={onClear}
        className="rounded-full border border-farm-border px-3 py-1 text-xs font-semibold text-farm-muted hover:bg-farm-cream hover:text-farm-brown"
      >
        Temizle
      </button>
    </div>
  );
}

export function FilterSortControls({
  activeDirection,
  onSort,
  ascLabel = "A-Z",
  descLabel = "Z-A",
}: {
  activeDirection?: "asc" | "desc" | null;
  onSort: (direction: "asc" | "desc") => void;
  ascLabel?: string;
  descLabel?: string;
}) {
  return (
    <div className="mb-3 grid grid-cols-2 gap-2 border-b border-farm-border pb-3">
      <button
        type="button"
        onClick={() => onSort("asc")}
        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
          activeDirection === "asc"
            ? "border-farm-earth bg-farm-earth-pale text-farm-brown"
            : "border-farm-border bg-farm-cream text-farm-muted hover:text-farm-brown"
        }`}
      >
        <i className="ri-sort-asc mr-1" />
        {ascLabel}
      </button>
      <button
        type="button"
        onClick={() => onSort("desc")}
        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
          activeDirection === "desc"
            ? "border-farm-earth bg-farm-earth-pale text-farm-brown"
            : "border-farm-border bg-farm-cream text-farm-muted hover:text-farm-brown"
        }`}
      >
        <i className="ri-sort-desc mr-1" />
        {descLabel}
      </button>
    </div>
  );
}
